import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RelatorioGovernanca } from '@/hooks/useGestorPerformance';

interface GovernanceChartProps {
  data: RelatorioGovernanca[];
}

const statusColors: Record<string, string> = {
  'validado': 'hsl(145, 50%, 40%)',
  'fechado': 'hsl(200, 65%, 50%)',
  'rascunho': 'hsl(38, 75%, 50%)',
  'enviado_por_prazo': 'hsl(0, 70%, 50%)'
};

const statusLabels: Record<string, string> = {
  'validado': 'Validado',
  'fechado': 'Fechado',
  'rascunho': 'Rascunho',
  'enviado_por_prazo': 'Prazo Vencido'
};

const GovernanceChart: React.FC<GovernanceChartProps> = ({ data }) => {
  // Agrupar por status
  const statusCounts = data.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const chartData = Object.entries(statusCounts).map(([status, count]) => ({
    name: statusLabels[status] || status,
    value: count,
    color: statusColors[status] || 'hsl(0, 0%, 50%)'
  }));

  // Calcular métricas de governança
  const totalRelatorios = data.length;
  const relatoriosValidados = statusCounts['validado'] || 0;
  const relatoriosProblematicos = (statusCounts['rascunho'] || 0) + (statusCounts['enviado_por_prazo'] || 0);
  const taxaValidacao = totalRelatorios > 0 ? Math.round((relatoriosValidados / totalRelatorios) * 100) : 0;

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">Governança de Relatórios</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 rounded-md bg-muted/50">
            <p className="text-lg font-semibold">{totalRelatorios}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="text-center p-2 rounded-md bg-success/10">
            <p className="text-lg font-semibold text-success">{taxaValidacao}%</p>
            <p className="text-xs text-muted-foreground">Validados</p>
          </div>
          <div className="text-center p-2 rounded-md bg-warning/10">
            <p className="text-lg font-semibold text-warning">{relatoriosProblematicos}</p>
            <p className="text-xs text-muted-foreground">Pendentes</p>
          </div>
        </div>
        {chartData.length === 0 ? (
          <div className="h-[150px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum relatório encontrado
          </div>
        ) : (
          <div className="h-[150px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  formatter={(value: number) => [value, 'Relatórios']}
                />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  formatter={(value) => (
                    <span className="text-xs text-foreground">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GovernanceChart;
